import { DashboardStats } from '../types';
import { databases, ensureAuth } from './appwriteClient';
import { Query } from 'appwrite';
import { getStudents, getTotalStudentCount } from './studentService';

const DATABASE_ID = '682acd26001f69683cd8';
const STUDENTS_COLLECTION_ID = '682acd33000e34dd93c0';
const ATTENDANCE_COLLECTION_ID = '683078a400301e04d0e8';

// Helper to format date to YYYY-MM-DD
const formatDateToYYYYMMDD = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Get dashboard statistics
export async function getDashboardStats(): Promise<DashboardStats> {
  try {
    // Get all students first to ensure we have the complete list
    const allStudents = await getStudents();
    console.log('Dashboard - All students fetched:', allStudents.length);
    console.log('Dashboard - Student IDs:', allStudents.map(s => s.id));

    // Use the actual length of students array as the total
    const totalStudents = allStudents.length;
    console.log('Dashboard - Total students count:', totalStudents);

    // Get today's attendance records
    const today = new Date().toISOString().split('T')[0];
    const attendanceResponse = await databases.listDocuments(
      DATABASE_ID,
      ATTENDANCE_COLLECTION_ID,
      [
        Query.equal('date', today),
        Query.limit(100)
      ]
    );

    // Map students with their attendance
    const studentsWithAttendance = allStudents.map(student => {
      // Find attendance for this student
      const studentAttendance = attendanceResponse.documents.find(
        (attendance: any) => attendance.student_id === student.id
      );

      return {
        ...student,
        attendance: studentAttendance ? [{
          id: studentAttendance.$id,
          Status: studentAttendance.Status,
          date: studentAttendance.date,
          remarks: studentAttendance.remarks || '',
          Created_at: studentAttendance.Created_at,
          Updated_at: studentAttendance.Updated_at
        }] : []
      };
    });

    // Calculate attendance statistics using the same logic as AttendanceSummaryPage
    const attendanceStats = {
      total: studentsWithAttendance.length,
      present: studentsWithAttendance.filter(student => 
        student.attendance.some(a => a.Status === 'present')
      ).length,
      absent: studentsWithAttendance.filter(student => 
        student.attendance.some(a => a.Status === 'absent')
      ).length,
      late: studentsWithAttendance.filter(student => 
        student.attendance.some(a => a.Status === 'late')
      ).length
    };

    const attendanceRate = attendanceStats.total > 0 
      ? Math.round((attendanceStats.present / attendanceStats.total) * 100)
      : 0;

    return {
      totalStudents,
      totalTeachers: 0, // TODO: Implement teacher count
      attendanceRate,
      feeCollection: {
        collected: 0,
        pending: 0,
        overdue: 0
      },
      todayAttendance: {
        total: attendanceStats.total,
        present: attendanceStats.present,
        absent: attendanceStats.absent,
        late: attendanceStats.late
      }
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
}

// Get monthly attendance data for charts
export const getMonthlyAttendanceData = async (period: '7days' | '30days' | 'month' = '7days') => {
  try {
    await ensureAuth();

    // Calculate date range based on period
    const now = new Date();
    let startDate: Date; // Inclusive start of the range
    let endDate: Date; // Inclusive end of the range for query and loop
    
    switch (period) {
      case '7days':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case '30days':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
        break;
    }

    // Format dates for query - MUST match database format (YYYY-MM-DD)
    const startDateStr = formatDateToYYYYMMDD(startDate);
    const endDateStr = formatDateToYYYYMMDD(endDate);

    console.log(`Querying attendance from ${startDateStr} to ${endDateStr}`);

    // Fetch total students count
    const studentsResponse = await databases.listDocuments(
      DATABASE_ID,
      STUDENTS_COLLECTION_ID
    );
    const totalStudents = studentsResponse.documents.length;

    // Fetch attendance records for the date range
    const attendanceResponse = await databases.listDocuments(
      DATABASE_ID,
      ATTENDANCE_COLLECTION_ID,
      [
        Query.between('date', startDateStr, endDateStr) // Use Query.between for inclusive range
        // Query.greaterThanEqual('date', startDateStr),
        // Query.lessThanEqual('date', endDateStr) 
      ]
    );

    // Create a map to store attendance by date
    const attendanceByDay = new Map<string, { present: number; total: number }>();

    // Initialize all dates in the range with zero counts
    let loopDate = new Date(startDate);
    while (loopDate <= endDate) { // Iterate up to and including the endDate
      const dateStr = formatDateToYYYYMMDD(loopDate);
      attendanceByDay.set(dateStr, { present: 0, total: 0 });
      loopDate.setDate(loopDate.getDate() + 1);
    }

    // Process attendance records
    attendanceResponse.documents.forEach((record: any) => {
      console.log('Raw record date from Appwrite:', record.date);
      const date = formatDateToYYYYMMDD(new Date(record.date));
      console.log('Processed date for map:', date);
      if (!attendanceByDay.has(date)) {
        attendanceByDay.set(date, { present: 0, total: 0 });
      }
      const stats = attendanceByDay.get(date)!;
      stats.total++;
      if (record.Status === 'present') {
        stats.present++;
      }
    });

    // Convert to array, sort by date, and calculate percentages
    console.log('Monthly Attendance Data before sorting:', Array.from(attendanceByDay.entries()));
    return Array.from(attendanceByDay.entries())
      .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
      .map(([date, stats]) => ({
        date,
        attendance: stats.total > 0 
          ? Math.round((stats.present / stats.total) * 100)
          : 0
      }));
  } catch (error) {
    console.error('Error fetching monthly attendance data:', error);
    throw error;
  }
};

// Get fee collection data for charts
export const getFeeCollectionData = async () => {
  try {
    await ensureAuth();

    // TODO: Implement fee collection data fetching
    return [
      { month: 'Jan', amount: 125000 },
      { month: 'Feb', amount: 150000 },
      { month: 'Mar', amount: 145000 },
      { month: 'Apr', amount: 160000 },
      { month: 'May', amount: 175000 },
      { month: 'Jun', amount: 180000 }
    ];
  } catch (error) {
    console.error('Error fetching fee collection data:', error);
    throw new Error('Failed to fetch fee collection data');
  }
};